import { useMemo, useRef, useState, useEffect } from "react";
import HistoryIcon from "../../assets/icons/approval 2.svg";
import { format, parse, parseISO } from "date-fns";
import { leaveTypes } from "../../constants/attendance";
import SickIcon from "../../assets/icons/leave_icons/sick_leave.svg";
import CasualIcon from "../../assets/icons/leave_icons/casual.svg";
import PrivilageIcon from "../../assets/icons/leave_icons/privilage_leave.svg";
import MaternityIcon from "../../assets/icons/leave_icons/maternity.svg";
import MenstrualIcon from "../../assets/icons/leave_icons/menstrual.svg";
import "./Calendar.css";
import CustomTooltip from "../../commonComponents/CustomToolTip";
import { useSelector } from "react-redux";
import { CalendarSkeletonLoader } from "../../utils/skeleton";

const days = ["SUN", "MON", "TUE", "WED", "THU", "FRI", "SAT"];

const months = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

const AttendanceCalendar = ({
  calendarData = [],
  calendarBanner,
  onOpenRegularisationModal = () => {},
  onOpenRegularisationHistoryModal = () => {},
  onOpenRegularisationHistoryNavChange = () => {},
  onMonthChange = () => {},
  role,
}) => {
  // console.log("====================================");
  // console.log(
  //   calendarData?.map((data) => {
  //     return { ...data?.status, date: data?.date };
  //   }),
  // );
  // console.log(calendarData);
  // console.log("====================================");
  const containerRef = useRef(null);
  const ref = useRef();
  const monthRefs = useRef([]);
  const yearRefs = useRef([]);

  const calendarDataResponseStatus = useSelector(
    (state) => state.attendance.calendarDataResponseStatus,
  );

  const today = new Date();
  const [month, setMonth] = useState(today.getMonth());
  const [year, setYear] = useState(today.getFullYear());
  const [viewMode, setViewMode] = useState("grid");
  const [popupData, setPopupData] = useState(null);
  const [open, setOpen] = useState(false);
  // console.log("====================================");
  // console.log(popupData);
  // console.log("====================================");

  const firstDay = new Date(year, month, 1).getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const daysInPrevMonth = new Date(year, month, 0).getDate();

  const years = Array.from(
    { length: 20 },
    (_, i) => today.getFullYear() - 10 + i,
  );

  useEffect(() => {
    const start = new Date(year, month, 1);
    const end = new Date(year, month + 1, 0);

    onMonthChange({
      month,
      year,
      start,
      end,
    });
  }, [month, year]);

  useEffect(() => {
    if (open) {
      monthRefs.current[month]?.scrollIntoView({
        block: "center",
        behavior: "auto",
      });

      const yearIndex = years.findIndex((y) => y === year);
      yearRefs.current[yearIndex]?.scrollIntoView({
        block: "center",
        behavior: "auto",
      });
    }
  }, [open, month, year, years]);

  const attendanceMap = useMemo(() => {
    const map = {};
    calendarData.forEach((item) => {
      map[item.date] = item;
      // if (item?.weekly_off) {
      //   alert(`You have applied for ${item.leave_type} on ${item.date}`);
      // }
      // if (item?.status?.leave_type) {
      //   alert(`You have applied for ${item.leave_type} on ${item.date}`);
      // }
    });
    return map;
  }, [calendarData]);

  const prevMonth = () => {
    setPopupData(null);
    if (month === 0) {
      setMonth(11);
      setYear((y) => y - 1);
    } else setMonth((m) => m - 1);
  };

  const nextMonth = () => {
    setPopupData(null);
    if (month === 11) {
      setMonth(0);
      setYear((y) => y + 1);
    } else setMonth((m) => m + 1);
  };

  const cells = useMemo(() => {
    return Array.from({ length: 42 }, (_, index) => {
      const row = Math.floor(index / 7);
      const col = index % 7;
      const dayNumber = index - firstDay + 1;

      let displayDate;
      let disabled = false;
      let cellMonth = month;
      let cellYear = year;

      if (dayNumber <= 0) {
        displayDate = daysInPrevMonth + dayNumber;
        disabled = true;

        if (month === 0) {
          cellMonth = 11;
          cellYear = year - 1;
        } else {
          cellMonth = month - 1;
        }
      } else if (dayNumber > daysInMonth) {
        displayDate = dayNumber - daysInMonth;
        disabled = true;

        if (month === 11) {
          cellMonth = 0;
          cellYear = year + 1;
        } else {
          cellMonth = month + 1;
        }
      } else {
        displayDate = dayNumber;
      }

      const formattedDate = `${cellYear}-${String(cellMonth + 1).padStart(
        2,
        "0",
      )}-${String(displayDate).padStart(2, "0")}`;

      const attendance = attendanceMap[formattedDate];

      const isToday =
        !disabled &&
        displayDate === today.getDate() &&
        month === today.getMonth() &&
        year === today.getFullYear();

      return {
        index,
        row,
        col,
        displayDate,
        disabled,
        isToday,
        formattedDate,
        attendance,
      };
    });
  }, [month, year, attendanceMap]);

  const listDays = useMemo(() => {
    return Array.from({ length: daysInMonth }, (_, i) => {
      const dateObj = new Date(year, month, i + 1);

      const formattedDate = `${year}-${String(month + 1).padStart(
        2,
        "0",
      )}-${String(i + 1).padStart(2, "0")}`;

      return {
        date: i + 1,
        dayName: days[dateObj.getDay()],
        isToday:
          i + 1 === today.getDate() &&
          month === today.getMonth() &&
          year === today.getFullYear(),
        attendance: attendanceMap[formattedDate],
      };
    });
  }, [month, year, attendanceMap]);

  const handlePopup = (e, cell) => {
    if (cell?.disabled) return;

    const cellRect = e.currentTarget.getBoundingClientRect();
    const containerRect = containerRef.current.getBoundingClientRect();

    const relativeTop = cellRect.top - containerRect.top;
    const relativeLeft = cellRect.left - containerRect.left;

    let position = "right";

    if (cell?.row <= 1) position = "bottom";
    else if (cell?.row >= 4) position = "top";
    else if (cell?.col === 6) position = "left";

    setPopupData({
      ...cell,
      top: relativeTop,
      left: relativeLeft,
      width: cellRect.width,
      height: cellRect.height,
      position,
    });
  };

  const getLeaveTypeDescription = (leaveType) => {
    const leaveTypePresent = leaveTypes.find(
      (type) => type.leave_type === leaveType,
    );
    return leaveTypePresent ? leaveTypePresent.description : leaveType;
  };

  const hasBanner =
    String(calendarBanner?.truid_status || "").toUpperCase() === "DOWN";

  const bannerText =
    "TRU ID is facing a connectivity issue. Your attendance data is safe and will be synced once the issue is resolved.";

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (!containerRef.current?.contains(e.target)) {
        setPopupData(null);
      }
      if (ref.current && !ref.current.contains(e.target)) {
        setOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const getPopupStyle = () => {
    if (!popupData) return {};

    const gap = 12;

    switch (popupData.position) {
      case "top":
        return {
          top: popupData.top - gap,
          left: popupData.left + popupData.width / 2,
        };
      case "bottom":
        return {
          top: popupData.top + popupData.height + gap,
          left: popupData.left + popupData.width / 2,
        };
      case "left":
        return {
          top: popupData.top + popupData.height / 2,
          left: popupData.left - gap,
        };
      default:
        return {
          top: popupData.top + popupData.height / 2,
          left: popupData.left + popupData.width + gap,
        };
    }
  };

  const getLeaveIcon = (type) => {
    switch (type) {
      case SickIcon:
        return "Sick";
      case "CL":
        return CasualIcon;
      case "PL":
        return PrivilageIcon;
      case "Maternity":
        return MaternityIcon;
      case "Menstrual":
        return MenstrualIcon;
      default:
        return CasualIcon;
    }
  };

  const StatusPill = ({ data, isList = false }) => {
    const status = data?.attendance?.status || {};

    const CommonResponse = (
      <>
        {/* Weekly Off */}
        {status?.compoff && (
          // <div className="cal-pop-weekly-wrap in-cell">
          <div
            className={`cal-pop-compoff ${status?.compoff === "sub" ? "orange" : "blue"}`}
          >
            Compoff{status?.compoff ? "-" + status?.compoff : ""}
          </div>
          // </div>
        )}
        {status?.weekly_off && (
          <div className="cal-pop-weekly-wrap in-cell">
            <div className="cal-pop-weekly-text in-cell">Weekly off</div>
          </div>
        )}

        {/* Holiday */}
        {status?.holiday && (
          <div className="cal-pop-holiday-wrap in-cell">
            <div className="icon-circle">
              <span
                style={{ fontSize: "12px", color: "white" }}
                className="material-symbols-rounded"
              >
                stars
              </span>
            </div>
            <div className="cal-pop-holiday-text in-cell">
              {status?.holiday}
            </div>
          </div>
        )}

        {/* Leave */}
        {status?.leave_type && (
          <div className="cal-pop-leave-wrap">
            <img
              className="cal-pop-leave-ico"
              src={getLeaveIcon(status?.leave_type)}
              alt={status?.leave_type}
            />
            <div className="cal-pop-leave-desc">
              {getLeaveTypeDescription(status?.leave_type)}
            </div>
          </div>
        )}

        {/* Attendance Status */}
        {!status?.holiday &&
          !status?.leave_type &&
          !status?.compoff &&
          !status?.weekly_off &&
          status?.att_status &&
          status?.att_status !== "N/A" && (
            <div
              className={`att-status-wrap ${
                status?.att_status === "present"
                  ? "att-present"
                  : status?.att_status === "absent"
                    ? "att-absent"
                    : "att-na-wrap"
              }`}
            >
              <div
                className={`att-status-text ${
                  status?.att_status === "N/A" ? "att-na-text" : ""
                }`}
              >
                {status?.att_status}
              </div>
            </div>
          )}
      </>
    );

    const knownKeys = ["holiday", "leave_type", "weekly_off", "compoff"];

    const extraKeys = knownKeys.filter((k) => k in status).length > 1;

    return (
      data?.attendance && (
        <>
          {isList ? (
            <>{CommonResponse}</>
          ) : extraKeys ? (
            <div className="flex items-center">
              {Object.keys(status).map(
                (key) =>
                  knownKeys.includes(key) && (
                    <div
                      className={`${key === "holiday" && "att-simple-wrap holiday-bg"} ${key === "leave_type" && "att-simple-wrap leave-bg"} ${(key === "compoff" || key === "weekly_off") && "att-simple-wrap"}`}
                    >
                      {key === "holiday" && (
                        <span
                          style={{ fontSize: "12px", color: "white" }}
                          className="material-symbols-rounded"
                        >
                          stars
                        </span>
                      )}
                      {key === "leave_type" && (
                        <img
                          className="att-simple-ico"
                          src={getLeaveIcon(status[key])}
                          alt=""
                        />
                      )}
                      {key === "compoff" && (
                        <div
                          key={key}
                          className={`att-circle ${
                            status?.compoff === "add" ||
                            status?.compoff === "paid"
                              ? "compoff-add-bg"
                              : status?.compoff === "sub"
                                ? "compoff-sub-bg"
                                : ""
                          }`}
                        ></div>
                      )}
                      {key === "weekly_off" && (
                        <div key={key} className="att-circle weekly-bg"></div>
                      )}
                      {key === "att_status" && (
                        <div
                          key={key}
                          className={`att-circle ${
                            status[key] === "present"
                              ? "att-present-clr"
                              : status[key] === "absent"
                                ? "att-absent-clr"
                                : status[key] === "half_day"
                                  ? "att-half-clr"
                                  : status[key] === "comp_off"
                                    ? "att-comp-clr"
                                    : ""
                          }`}
                        ></div>
                      )}
                    </div>
                  ),
              )}
            </div>
          ) : (
            <>{CommonResponse}</>
          )}
          <></>
        </>
      )
    );
  };

  return (
    <>
      {calendarDataResponseStatus === "PENDING" && <CalendarSkeletonLoader />}
      {calendarDataResponseStatus === "SUCCESS" && (
        <div className="calendar-container" ref={containerRef}>
          <header className="calendar-header">
            <div className="calendar-left">
              <div className="cl-nav-btn" onClick={prevMonth}>
                <span class="material-symbols-outlined">arrow_left</span>
              </div>

              <div className="picker-container" ref={ref}>
                <div className="picker-header" onClick={() => setOpen(!open)}>
                  {months[month]} {year}
                </div>

                {open && (
                  <div className="picker-dropdown">
                    <div className="picker-column">
                      {months.map((m, i) => (
                        <div
                          key={m}
                          ref={(el) => (monthRefs.current[i] = el)}
                          className={`picker-item ${month === i ? "active" : ""}`}
                          onClick={() => setMonth(i)}
                        >
                          <div className="picker-item-text">{m}</div>
                        </div>
                      ))}
                    </div>

                    <div className="picker-column">
                      {years.map((y, i) => (
                        <div
                          key={y}
                          ref={(el) => (yearRefs.current[i] = el)}
                          className={`picker-item ${year === y ? "active" : ""}`}
                          onClick={() => setYear(y)}
                        >
                          <div className="picker-item-text">{y}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              <div className="cl-nav-btn" onClick={nextMonth}>
                <span class="material-symbols-outlined">arrow_right</span>
              </div>
            </div>

            <div className="calendar-view-toggle">
              <button
                className={viewMode === "grid" ? "active" : ""}
                aria-label="Grid view"
                onClick={() => setViewMode("grid")}
              >
                <span className="material-symbols-rounded">grid_on</span>
              </button>
              <button
                className={viewMode === "list" ? "active" : ""}
                aria-label="List view"
                onClick={() => {
                  setViewMode("list");
                  setPopupData(null);
                }}
              >
                <span className="material-symbols-rounded">list</span>
              </button>
            </div>

            {hasBanner && (
              <div className="calendar-banner" title={bannerText}>
                <span className="material-symbols-rounded calendar-banner-icon">
                  info
                </span>
                <div className="calendar-banner-text">{bannerText}</div>
              </div>
            )}

            <div className="regularistion-tabs">
              {role === "Manager" && (
                <CustomTooltip text="Regularisation Approvals">
                  <div
                    className="regularize-approval-btn"
                    onClick={onOpenRegularisationHistoryNavChange}
                  >
                    <img
                      className="regularize-approval-btn-ico"
                      src={HistoryIcon}
                      alt="History"
                    />
                  </div>
                </CustomTooltip>
              )}
              <div
                className="regularize-history-btn"
                onClick={onOpenRegularisationHistoryModal}
              >
                <div className="regularize-history-btn-text">History</div>
              </div>
              <div
                className="regularize-btn"
                onClick={onOpenRegularisationModal}
              >
                <div className="regularize-btn-text">Regularise</div>
              </div>
            </div>
          </header>

          {viewMode === "grid" && (
            <>
              <div className="calendar-days">
                {days.map((d) => (
                  <div key={d} className="calendar-day-header">
                    {d}
                  </div>
                ))}
              </div>
              <div className="calendar-grid">
                {cells.map((cell) => (
                  <div
                    key={cell?.index}
                    className={`att-calendar-cell 
                ${popupData?.attendance?.date === cell?.formattedDate ? "active" : ""}
                ${cell?.disabled ? "disabled" : ""}
                ${cell?.isToday ? "today" : ""}
                ${cell?.attendance?.status?.att_status || ""}`}
                    style={{
                      backgroundColor: cell?.attendance?.color || "",
                    }}
                    onClick={(e) => handlePopup(e, cell)}
                  >
                    <div className="att-date">{cell?.displayDate}</div>

                    <StatusPill data={cell} />
                  </div>
                ))}
              </div>
            </>
          )}

          {viewMode === "list" && (
            <div className="calendar-list">
              {listDays.map((day) => {
                const showListMiddle =
                  !!day?.attendance &&
                  (day?.attendance?.clock_in ||
                    day?.attendance?.clock_out ||
                    day?.attendance?.status?.leave_type ||
                    (day?.attendance?.status?.att_status &&
                      day?.attendance?.status?.att_status !== "N/A"));

                const showListRight =
                  !!day?.attendance?.status &&
                  (day?.attendance?.status?.holiday ||
                    day?.attendance?.status?.leave_type ||
                    day?.attendance?.status?.weekly_off ||
                    day?.attendance?.status?.compoff ||
                    (day?.attendance?.status?.att_status &&
                      day?.attendance?.status?.att_status !== "N/A"));

                const rowClass =
                  `list-row ${!showListMiddle && !showListRight ? "list-row-empty" : ""}`.trim();

                return (
                  <div key={day.date} className={rowClass}>
                    <div className="list-left">
                      <div className="list-day-wrap">
                        <span className="list-day">{day.dayName}</span>
                      </div>
                      <span className="list-date">
                        {day.date} {months[month].slice(0, 3)} '
                        {String(year).slice(-2)}
                      </span>
                      <span className="today-row">
                        {day.isToday && "Today"}
                      </span>
                    </div>
                    {showListMiddle && (
                      <div className="list-middle">
                        <div
                          className={`clock-pill-wrap ${
                            day?.attendance?.status?.leave_type ||
                            (!day?.attendance?.clock_in &&
                              day?.attendance?.status?.att_status === "absent")
                              ? "grey-out"
                              : ""
                          }`}
                        >
                          <span
                            style={{ fontSize: "12px", color: "black" }}
                            class="material-symbols-rounded"
                          >
                            login
                          </span>
                          <div class="clock-pill-text">
                            {day?.attendance?.clock_in &&
                              format(
                                parse(
                                  day?.attendance?.clock_in,
                                  "HH:mm:ss",
                                  new Date(),
                                ),
                                "h:mm a",
                              )}
                            {!day?.attendance?.clock_in &&
                              day?.attendance?.status?.att_status ===
                                "absent" &&
                              "Absent"}
                            {day?.attendance?.status?.leave_type && "Leave"}
                          </div>
                        </div>
                        <div
                          className={`clock-pill-wrap ${
                            day?.attendance?.status?.leave_type ||
                            (!day?.attendance?.clock_in &&
                              day?.attendance?.status?.att_status === "absent")
                              ? "grey-out"
                              : ""
                          }`}
                        >
                          <span
                            style={{ fontSize: "12px", color: "black" }}
                            class="material-symbols-rounded"
                          >
                            logout
                          </span>
                          <div class="clock-pill-text">
                            {day?.attendance?.clock_out &&
                              format(
                                parse(
                                  day?.attendance?.clock_out,
                                  "HH:mm:ss",
                                  new Date(),
                                ),
                                "h:mm a",
                              )}
                            {!day?.attendance?.clock_out &&
                              day?.attendance?.status?.att_status ===
                                "absent" &&
                              "Absent"}
                            {day?.attendance?.status?.leave_type && "Leave"}
                          </div>
                        </div>
                      </div>
                    )}

                    {showListRight && (
                      <div className="list-right">
                        <StatusPill data={day} isList={true} />
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {popupData && (
            <div
              className={`calendar-popup ${popupData.position}`}
              style={getPopupStyle()}
            >
              <div className="flex justify-between items-center w-full">
                <div className="cal-pop-head">
                  {format(parseISO(popupData?.attendance?.date), "dd MMM ''yy")}
                </div>
                <div
                  class="icon-circle-wrap"
                  onClick={() => setPopupData(null)}
                >
                  <span
                    style={{ fontSize: "16px", color: "black" }}
                    class="material-symbols-rounded"
                  >
                    close
                  </span>
                </div>
              </div>

              {popupData?.attendance.status?.holiday && (
                <div className="cal-pop-holiday-wrap">
                  <div class="icon-circle">
                    <span
                      style={{ fontSize: "12px", color: "white" }}
                      class="material-symbols-rounded"
                    >
                      stars
                    </span>
                  </div>
                  <div className="cal-pop-holiday-text">
                    {popupData.attendance.status?.holiday}
                  </div>
                </div>
              )}
              {popupData?.attendance?.status?.compoff && (
                <div className="cal-pop-comp-wrap">
                  <div
                    className={`cal-pop-compoff ${popupData.attendance.status?.compoff === "sub" ? "orange" : "blue"}`}
                  >
                    {" "}
                    Compoff
                    {popupData.attendance.status?.compoff
                      ? "-" + popupData.attendance.status?.compoff
                      : ""}
                  </div>
                </div>
              )}
              {popupData?.attendance?.status?.weekly_off && (
                <div className="cal-pop-comp-wrap">
                  <div className={`cal-pop-weekly`}> Weekend</div>
                </div>
              )}
              {popupData?.attendance.status?.leave_type && (
                <div className="cal-pop-leave">
                  <div className="cal-pop-leave-wrap">
                    <span
                      style={{ fontSize: "12px", color: "#A78AFE" }}
                      class="material-symbols-rounded"
                    >
                      approval
                    </span>
                    <div className="cal-pop-leave-desc">
                      {getLeaveTypeDescription(
                        popupData.attendance.status?.leave_type,
                      )}
                    </div>
                  </div>
                  <div className="cal-pop-leave-details">
                    Applied on{" "}
                    {format(
                      parseISO(popupData?.attendance?.applied_date),
                      "dd MMM ''yy",
                    )}
                  </div>
                  <div className="cal-pop-leave-alert">
                    <span
                      style={{ fontSize: "16px", color: "#FA4B16" }}
                      class="material-symbols-rounded"
                    >
                      notifications_active
                    </span>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </>
  );
};

export default AttendanceCalendar;
